import React from 'react';
import { Box, Typography, Avatar, Tooltip } from '@mui/material';
import { format } from 'date-fns';

/**
 * Individual message display — shows as a chat bubble.
 * Own messages align right (primary color), others align left (grey).
 */
const MessageBubble = ({ message, isOwn, showAuthor = true }) => {
  const timestamp = message.timestamp?.toDate
    ? message.timestamp.toDate()
    : message.timestamp ? new Date(message.timestamp) : null;

  const isDeleted = !!message.deletedAt;
  const isEdited = !!message.editedAt;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isOwn ? 'row-reverse' : 'row',
        alignItems: 'flex-end',
        mb: 1,
        gap: 1
      }}
    >
      {/* Avatar — only show for other people's messages */}
      {!isOwn && showAuthor && (
        <Avatar
          sx={{ width: 28, height: 28, fontSize: '0.75rem' }}
          src={message.authorPhoto}
        >
          {message.authorName?.charAt(0)?.toUpperCase()}
        </Avatar>
      )}

      {/* Spacer when avatar is hidden but alignment is needed */}
      {!isOwn && !showAuthor && <Box sx={{ width: 28 }} />}

      <Box sx={{ maxWidth: '70%' }}>
        {/* Author name — only show for group/family chats, not direct */}
        {!isOwn && showAuthor && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1, mb: 0.25, display: 'block' }}>
            {message.authorName}
          </Typography>
        )}

        {/* Message bubble */}
        <Tooltip
          title={timestamp ? format(timestamp, 'MMM d, yyyy h:mm a') : ''}
          placement={isOwn ? 'left' : 'right'}
        >
          <Box
            sx={{
              px: message.type === 'image' && !isDeleted ? 0.5 : 2,
              py: message.type === 'image' && !isDeleted ? 0.5 : 1,
              borderRadius: 2,
              borderTopRightRadius: isOwn ? 4 : 16,
              borderTopLeftRadius: isOwn ? 16 : 4,
              bgcolor: isDeleted
                ? 'action.disabledBackground'
                : isOwn
                  ? 'primary.main'
                  : 'grey.100',
              color: isDeleted
                ? 'text.disabled'
                : isOwn
                  ? 'primary.contrastText'
                  : 'text.primary',
              fontStyle: isDeleted ? 'italic' : 'normal',
              overflow: 'hidden'
            }}
          >
            {isDeleted ? (
              <Typography variant="body2" sx={{ px: message.type === 'image' ? 1.5 : 0, py: message.type === 'image' ? 0.5 : 0 }}>
                This message was deleted
              </Typography>
            ) : message.type === 'image' && message.imageUrl ? (
              <>
                <img
                  src={message.imageUrl}
                  alt="Shared"
                  style={{
                    maxWidth: '100%',
                    maxHeight: 300,
                    borderRadius: 8,
                    display: 'block',
                    cursor: 'pointer'
                  }}
                  onClick={() => window.open(message.imageUrl, '_blank')}
                />
                {message.content && (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', px: 1.5, py: 0.5, color: isOwn ? 'primary.contrastText' : 'text.primary' }}>
                    {message.content}
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {message.content}
              </Typography>
            )}
          </Box>
        </Tooltip>

        {/* Timestamp + edited indicator */}
        <Box sx={{ display: 'flex', justifyContent: isOwn ? 'flex-end' : 'flex-start', gap: 0.5, mt: 0.25 }}>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
            {timestamp ? format(timestamp, 'h:mm a') : ''}
          </Typography>
          {isEdited && !isDeleted && (
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
              (edited)
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MessageBubble;
